import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { onboardingService } from '../services/onboardingService';
import { ONBOARDING_SECTIONS, type OnboardingProgress, type OnboardingSection, type SectionStatus } from '../types/onboarding';

interface OnboardingContextValue {
  progress: OnboardingProgress;
  sections: OnboardingSection[];
  completedCount: number;
  totalCount: number;
  activeCount: number;
  percentComplete: number;
  getSectionStatus: (sectionId: string) => SectionStatus;
  setSectionStatus: (sectionId: string, status: SectionStatus) => void;
  markSectionViewed: (sectionId: string) => void;
  markSectionComplete: (sectionId: string) => void;
  markSectionIncomplete: (sectionId: string) => void;
  isSectionComplete: (sectionId: string) => boolean;
  getSectionById: (sectionId: string) => OnboardingSection | undefined;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<OnboardingProgress>(() =>
    onboardingService.getProgress()
  );

  const sections = ONBOARDING_SECTIONS;
  const activeSections = sections.filter(s => !s.isComingSoon);
  const activeCount = activeSections.length;
  const completedCount = activeSections.filter(s =>
    progress.sections[s.id]?.status === 'complete'
  ).length;
  const totalCount = sections.length;
  const percentComplete = activeCount > 0 ? Math.round((completedCount / activeCount) * 100) : 0;

  const getSectionStatus = useCallback((sectionId: string): SectionStatus => {
    return progress.sections[sectionId]?.status || 'unseen';
  }, [progress.sections]);

  const setSectionStatus = useCallback((sectionId: string, status: SectionStatus) => {
    const newProgress = onboardingService.setSectionStatus(sectionId, status);
    setProgress({ ...newProgress });
  }, []);

  const markSectionViewed = useCallback((sectionId: string) => {
    const newProgress = onboardingService.markSectionViewed(sectionId);
    setProgress({ ...newProgress });
  }, []);

  const markSectionComplete = useCallback((sectionId: string) => {
    const newProgress = onboardingService.markSectionComplete(sectionId);
    setProgress({ ...newProgress });
  }, []);

  const markSectionIncomplete = useCallback((sectionId: string) => {
    const newProgress = onboardingService.markSectionIncomplete(sectionId);
    setProgress({ ...newProgress });
  }, []);

  const isSectionComplete = useCallback((sectionId: string) => {
    return progress.sections[sectionId]?.status === 'complete';
  }, [progress.sections]);

  const getSectionById = useCallback((sectionId: string) => {
    return sections.find(s => s.id === sectionId);
  }, [sections]);

  useEffect(() => {
    const stored = onboardingService.getProgress();
    setProgress(stored);
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        progress,
        sections,
        completedCount,
        totalCount,
        activeCount,
        percentComplete,
        getSectionStatus,
        setSectionStatus,
        markSectionViewed,
        markSectionComplete,
        markSectionIncomplete,
        isSectionComplete,
        getSectionById,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
